// ----------------------------------------------------------------------------
// Snippet: Spring Calculator
// ----------------------------------------------------------------------------
//
// Intercepts the product form submit on eligible shock PDPs, opens the spring
// calculator modal, recommends a spring rate and lets the customer add the
// shock together with one or more matching springs in a single cart call.
//
// Built as a standalone bundle (assets/spring-calculator.js + .css) with no
// dependency on the host theme's scripts, so it can be reused on other FOX
// storefronts. The modal markup and configuration come from
// spring-calculator.liquid. The maths lives in spring-calculator-engine.js and
// the catalogue matching in spring-matcher.js, so this file only deals with
// the DOM and the cart.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import { calculateSpringRate, describeRidingStyle, LBS_PER_KG } from './spring-calculator-engine';
import { findCompatibleSprings, getMaxTlg, indexVariantsBySku, resolveSpringVariants } from './spring-matcher';
import { parseShockDimensions, nearestStroke } from './shock-variant';
import { createStoreLookup } from './spring-store-lookup';

// Styles
import './_spring-calculator.scss';

// -------------------------------------
// Constants
// -------------------------------------
const PRODUCT_FORM_SELECTOR = 'form[data-product-form], form[action$="/cart/add"]';
const OPEN_CLASS = 'is-open';
const BODY_LOCK_CLASS = 'spring-calculator-open';
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const STROKE_PREFILL_TOLERANCE_MM = 2.5;

/**
 * Convert the product form into a /cart/add.js line item. Mirrors the field
 * handling in snippets/cart-js/ajax-add-cart.js.
 */
const formToLineItem = (form) => {
    const item = {};
    const properties = {};

    new FormData(form).forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        if (propertyMatch) {
            properties[propertyMatch[1]] = value;
        } else if (key === 'id' || key === 'quantity' || key === 'selling_plan') {
            item[key] = value;
        }
    });

    item.quantity = parseInt(item.quantity, 10) || 1;
    if (Object.keys(properties).length) item.properties = properties;

    return item;
};

const show = (element, visible = true) => {
    if (element) element.hidden = !visible;
};

// -------------------------------------
// Calculator
// -------------------------------------
class SpringCalculator {
    constructor(modal) {
        this.modal = modal;
        this.config = JSON.parse(document.querySelector('[data-spring-calculator-config]').textContent);
        this.strings = this.config.strings;
        this.productForm = document.querySelector(PRODUCT_FORM_SELECTOR);
        this.lastFocused = null;

        this.data = null;
        this.dataPromise = null;
        this.calculationId = 0;

        // Springs are resolved to store variants by SKU through the storefront
        // search endpoints. Products picked in theme settings act as a fallback.
        this.storeLookup = createStoreLookup({
            predictiveSearchUrl: this.config.predictiveSearchUrl,
            rootUrl: this.config.rootUrl,
            moneyFormat: this.config.moneyFormat,
        });
        this.fallbackVariantIndex = indexVariantsBySku(this.config.springVariants);
        this.selectedVariantIds = new Set();
        this.hasResult = false;

        this.cacheElements();
        this.bindEvents();
    }

    cacheElements() {
        const q = (selector) => this.modal.querySelector(selector);

        this.el = {
            form: q('[data-calculator-form]'),
            shockSummary: q('[data-shock-summary]'),
            shockTitle: q('[data-shock-title]'),
            riderWeight: q('[data-rider-weight]'),
            unitToggle: q('[data-unit-toggle]'),
            unitLabel: q('[data-weight-unit-label]'),
            rearTravel: q('[data-rear-travel]'),
            ebikeToggle: q('[data-ebike-toggle]'),
            stroke: q('[data-stroke]'),
            strokeHint: q('[data-stroke-hint]'),
            shockModel: q('[data-shock-model]'),
            proToggle: q('[data-pro-toggle]'),
            proNote: q('[data-pro-note]'),
            ridingStyle: q('[data-riding-style]'),
            styleMiddleLabel: q('[data-style-middle-label]'),
            formError: q('[data-form-error]'),
            results: q('[data-results]'),
            resultRate: q('[data-result-rate]'),
            resultSag: q('[data-result-sag]'),
            resultDescription: q('[data-result-description]'),
            matchesWrapper: q('[data-matches-wrapper]'),
            matchesLoading: q('[data-matches-loading]'),
            matches: q('[data-matches]'),
            noMatch: q('[data-no-match]'),
            cartError: q('[data-cart-error]'),
            addWithSprings: q('[data-add-with-springs]'),
            addShockOnly: q('[data-add-shock-only]'),
        };

        this.addWithSpringsLabel = this.el.addWithSprings.textContent.trim();
        this.addShockOnlyLabel = this.el.addShockOnly.textContent.trim();
    }

    bindEvents() {
        // Capture phase so we run before the ajax add-to-cart handler bound on the form.
        document.addEventListener('submit', (event) => this.onProductFormSubmit(event), true);

        this.el.form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.calculate();
        });

        this.el.unitToggle.addEventListener('change', () => this.onUnitToggle());
        this.el.proToggle.addEventListener('change', () => this.onProToggle());

        // Once a result is on screen, keep it in sync with the inputs.
        [this.el.ebikeToggle, this.el.stroke, this.el.shockModel, this.el.ridingStyle].forEach((input) => {
            input.addEventListener('change', () => this.recalculateIfShown());
        });
        [this.el.riderWeight, this.el.rearTravel].forEach((input) => {
            let timeout;
            input.addEventListener('input', () => {
                clearTimeout(timeout);
                timeout = setTimeout(() => this.recalculateIfShown(), 400);
            });
        });

        this.el.matches.addEventListener('click', (event) => {
            const button = event.target.closest('[data-select-spring]');
            if (button) this.toggleSpring(button);
        });

        this.el.addWithSprings.addEventListener('click', () => this.addToCart(true));
        this.el.addShockOnly.addEventListener('click', () => this.addToCart(false));

        // Modal shell
        this.modal.querySelectorAll('[data-close]').forEach((element) => {
            element.addEventListener('click', () => this.close());
        });
        document.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    // -------------------------------------
    // Modal shell
    // -------------------------------------

    isOpen() {
        return this.modal.classList.contains(OPEN_CLASS);
    }

    showModal() {
        this.lastFocused = document.activeElement;
        this.modal.classList.add(OPEN_CLASS);
        this.modal.removeAttribute('aria-hidden');
        document.body.classList.add(BODY_LOCK_CLASS);
    }

    close() {
        if (!this.isOpen()) return;
        this.modal.classList.remove(OPEN_CLASS);
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove(BODY_LOCK_CLASS);
        this.lastFocused?.focus?.();
    }

    onKeydown(event) {
        if (!this.isOpen()) return;

        if (event.key === 'Escape') {
            this.close();
            return;
        }

        if (event.key === 'Tab') {
            const focusable = [...this.modal.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    }

    // -------------------------------------
    // Opening
    // -------------------------------------

    onProductFormSubmit(event) {
        if (event.target !== this.productForm) return;

        event.preventDefault();
        event.stopPropagation();
        this.open();
    }

    async open() {
        this.resetResults();
        show(this.el.cartError, false);
        show(this.el.formError, false);

        this.showModal();

        try {
            await this.loadData();
        } catch (error) {
            this.showError(this.el.formError, this.strings.errorData);
            console.error('Spring calculator data failed to load', error);
            return;
        }

        this.populateOptions();
        this.prefillFromVariant();
        this.el.riderWeight.focus();
    }

    loadData() {
        if (this.data) return Promise.resolve(this.data);

        if (!this.dataPromise) {
            this.dataPromise = fetch(this.config.dataUrl)
                .then((response) => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response.json();
                })
                .then((data) => {
                    this.data = data;
                    return data;
                })
                .catch((error) => {
                    this.dataPromise = null;
                    throw error;
                });
        }

        return this.dataPromise;
    }

    populateOptions() {
        if (this.el.stroke.options.length) return;

        this.data.strokeOptions.forEach((stroke) => {
            this.el.stroke.add(new Option(`${stroke} mm`, String(stroke)));
        });

        this.data.shockModels.forEach((model) => {
            this.el.shockModel.add(new Option(model.label, model.key));
        });
    }

    currentShockVariant() {
        const currentId = parseInt(this.productForm.querySelector('[name="id"]')?.value, 10);
        return this.config.shockVariants.find((variant) => variant.id === currentId) || null;
    }

    prefillFromVariant() {
        const variant = this.currentShockVariant();
        if (!variant) return;

        const { strokeMm, eyeToEyeMm } = parseShockDimensions(variant);

        const summary = [this.config.productTitle, variant.title !== 'Default Title' ? variant.title : null]
            .filter(Boolean).join(' – ');
        this.el.shockTitle.textContent = summary;
        show(this.el.shockSummary, true);

        // Only prefill when the variant's stroke is close to a catalogue stroke;
        // an out-of-range shock should not be silently snapped to the nearest option.
        const matched = strokeMm ? nearestStroke(this.data.strokeOptions, strokeMm) : null;
        const isClose = matched !== null && Math.abs(matched - strokeMm) <= STROKE_PREFILL_TOLERANCE_MM;

        this.el.stroke.value = isClose ? String(matched) : '';
        show(this.el.strokeHint, isClose);

        this.eyeToEyeMm = eyeToEyeMm;
    }

    // -------------------------------------
    // Inputs
    // -------------------------------------

    isMetric() {
        return this.el.unitToggle.checked;
    }

    onUnitToggle() {
        const current = parseFloat(this.el.riderWeight.value);
        this.el.unitLabel.textContent = this.isMetric() ? '(kg)' : '(lbs)';

        if (!Number.isNaN(current)) {
            this.el.riderWeight.value = Math.round(this.isMetric() ? current / LBS_PER_KG : current * LBS_PER_KG);
        }
        this.recalculateIfShown();
    }

    onProToggle() {
        const isPro = this.el.proToggle.checked;
        this.el.ridingStyle.value = 50;
        this.el.styleMiddleLabel.textContent = isPro ? this.strings.pro : this.strings.suggested;
        show(this.el.proNote, isPro);
        this.recalculateIfShown();
    }

    readInputs() {
        const riderWeight = parseFloat(this.el.riderWeight.value);
        const rearWheelTravelMm = parseFloat(this.el.rearTravel.value);
        const strokeMm = parseFloat(this.el.stroke.value);

        if ([riderWeight, rearWheelTravelMm, strokeMm].some((value) => Number.isNaN(value) || value <= 0)) {
            return null;
        }

        return {
            riderWeightLbs: this.isMetric() ? riderWeight * LBS_PER_KG : riderWeight,
            rearWheelTravelMm,
            strokeMm,
            isEbike: this.el.ebikeToggle.checked,
            isPro: this.el.proToggle.checked,
            ridingStyle: parseFloat(this.el.ridingStyle.value),
            shockModelKey: this.el.shockModel.value,
        };
    }

    // -------------------------------------
    // Calculation and results
    // -------------------------------------

    recalculateIfShown() {
        if (this.hasResult) this.calculate();
    }

    async calculate() {
        const input = this.readInputs();

        if (!input || !this.data) {
            this.showError(this.el.formError, this.strings.errorRequired);
            return;
        }
        show(this.el.formError, false);

        const result = calculateSpringRate(input);
        const shockModel = this.data.shockModels.find((model) => model.key === input.shockModelKey);
        const maxTlg = getMaxTlg(shockModel, input.strokeMm);
        const compatible = findCompatibleSprings(this.data.springs, { strokeMm: input.strokeMm, rate: result.rate, maxTlg });

        this.renderResult(result, input);
        this.hasResult = true;

        // Resolve part numbers to store variants. The id guards against a
        // slower, older lookup overwriting the result of a newer calculation.
        const calculationId = ++this.calculationId;
        this.setMatchesLoading(true);

        const storeIndex = compatible.length
            ? await this.storeLookup.resolveSkus(compatible.map((spring) => spring.partNumber))
            : {};
        if (calculationId !== this.calculationId) return;

        const matches = resolveSpringVariants(compatible, { ...this.fallbackVariantIndex, ...storeIndex });
        this.setMatchesLoading(false);
        this.renderMatches(matches);
    }

    setMatchesLoading(loading) {
        this.selectedVariantIds.clear();
        this.el.matches.innerHTML = '';
        show(this.el.matchesLoading, loading);
        show(this.el.matchesWrapper, loading);
        show(this.el.noMatch, false);
        this.updateFooter();
    }

    renderResult(result, input) {
        this.el.resultRate.textContent = result.rate;
        this.el.resultSag.textContent = this.strings.sagSummary
            .replace('{{ target }}', result.targetSagPercent)
            .replace('{{ actual }}', result.actualSagPercent);
        this.el.resultDescription.textContent = describeRidingStyle(input.ridingStyle, input.isPro);
        show(this.el.results, true);
    }

    renderMatches(matches) {
        this.selectedVariantIds.clear();
        this.el.matches.innerHTML = '';

        matches.forEach(({ spring, variant }) => {
            const item = document.createElement('li');
            item.className = 'spring-calculator__match';
            item.innerHTML = `
                <div class="spring-calculator__match-image">
                    ${variant.image ? `<img src="${variant.image}" alt="" loading="lazy">` : ''}
                </div>
                <div class="spring-calculator__match-info">
                    <span class="spring-calculator__match-title">${spring.type} – ${variant.productTitle}</span>
                    <span class="spring-calculator__match-variant">${variant.title}</span>
                    <span class="spring-calculator__match-meta">${this.strings.partNumber} ${spring.partNumber}</span>
                    <span class="spring-calculator__match-price">${variant.price}</span>
                </div>
                <div class="spring-calculator__match-action">
                    <button type="button" class="button button--secondary" data-select-spring data-variant-id="${variant.id}" ${variant.available ? '' : 'disabled'}>
                        ${variant.available ? this.strings.select : this.strings.soldOut}
                    </button>
                </div>
            `;
            this.el.matches.appendChild(item);
        });

        show(this.el.matchesWrapper, matches.length > 0);
        show(this.el.noMatch, matches.length === 0);
        this.updateFooter();
    }

    toggleSpring(button) {
        const variantId = button.dataset.variantId;
        const item = button.closest('.spring-calculator__match');
        const nowSelected = !this.selectedVariantIds.has(variantId);

        if (nowSelected) {
            this.selectedVariantIds.add(variantId);
        } else {
            this.selectedVariantIds.delete(variantId);
        }

        item.classList.toggle('is-selected', nowSelected);
        button.textContent = nowSelected ? this.strings.selected : this.strings.select;
        button.classList.toggle('button--primary', nowSelected);
        button.classList.toggle('button--secondary', !nowSelected);
        this.updateFooter();
    }

    updateFooter() {
        this.el.addWithSprings.disabled = this.selectedVariantIds.size === 0;
    }

    resetResults() {
        this.hasResult = false;
        this.selectedVariantIds.clear();
        this.el.matches.innerHTML = '';
        show(this.el.results, false);
        show(this.el.matchesWrapper, false);
        show(this.el.matchesLoading, false);
        show(this.el.noMatch, false);
        this.updateFooter();
    }

    showError(element, message) {
        element.textContent = message;
        show(element, true);
    }

    // -------------------------------------
    // Cart
    // -------------------------------------

    async addToCart(includeSprings) {
        const items = [formToLineItem(this.productForm)];

        if (includeSprings) {
            this.selectedVariantIds.forEach((variantId) => {
                items.push({ id: variantId, quantity: 1 });
            });
        }

        this.setBusy(true);
        show(this.el.cartError, false);

        try {
            const response = await fetch(this.config.cartAddUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items }),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.description || error.message || this.strings.errorCart);
            }

            window.location.href = this.config.cartUrl;
        } catch (error) {
            this.showError(this.el.cartError, error.message || this.strings.errorCart);
            this.setBusy(false);
        }
    }

    setBusy(busy) {
        this.el.addWithSprings.disabled = busy || this.selectedVariantIds.size === 0;
        this.el.addShockOnly.disabled = busy;
        this.el.addWithSprings.textContent = busy ? this.strings.adding : this.addWithSpringsLabel;
        this.el.addShockOnly.textContent = busy ? this.strings.adding : this.addShockOnlyLabel;
    }
}

// -------------------------------------
// Hooks
// -------------------------------------
const init = () => {
    const modal = document.querySelector('[data-spring-calculator]');
    if (modal && document.querySelector(PRODUCT_FORM_SELECTOR)) {
        new SpringCalculator(modal);
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
